import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
  Fab,
  Menu,
  MenuItem,
  Alert,
  Avatar,
  Stack,
  Badge,
  Divider,
  Button,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Pagination,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  MoreVert,
  Block,
  Edit,
  Visibility,
  Close,
  CheckCircle,
  FileUpload,
  Phone,
  WhatsApp,
  Person,
  ErrorOutline,
} from '@mui/icons-material';
import api from '../../utils/api';
import StudentDetailDrawer from './StudentDetailDrawer';
import StudentFormDrawer from './StudentFormDrawer';
import BulkImportDrawer from './BulkImportDrawer';
import StudentFilterDrawer from './StudentFilterDrawer';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const DEFAULT_FILTERS = { status: 'all', has_active_booking: false, sort: '' };

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function StudentManagement({ showSnackbar }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });

  // Drawer state
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState(0);

  // Row overflow menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuStudent, setMenuStudent] = useState(null);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, filters]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters, pagination.page]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setFetchError(false);

      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.has_active_booking) params.append('has_active_booking', 'true');
      // sort param sent; backend may ignore it — client-side fallback below
      if (filters.sort) params.append('sort', filters.sort);

      const res = await api.get(`/librarian/students?${params.toString()}`);
      let list = res.data.data.students;

      // Client-side sort fallback in case backend ignores ?sort
      if (filters.sort === 'name_asc') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
      else if (filters.sort === 'name_desc') list = [...list].sort((a, b) => b.name.localeCompare(a.name));

      setStudents(list);
      setPagination(res.data.data.pagination);
    } catch {
      setFetchError(true);
      showSnackbar('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (student, initialTab = 0) => {
    setSelectedStudent(student);
    setDetailInitialTab(initialTab);
    setDetailDrawerOpen(true);
  };

  const handleStudentCreated = (createdStudent = null) => {
    fetchStudents();
    setFormDrawerOpen(false);
    if (createdStudent) {
      setSelectedStudent(createdStudent);
      setDetailInitialTab(2); // Payments tab
      setDetailDrawerOpen(true);
    }
  };

  const handleMenuOpen = (e, student) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuStudent(student);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuStudent(null);
  };

  const handleBlockStudent = async () => {
    try {
      await api.put(`/librarian/students/${menuStudent.id}/block`, { blocked: !menuStudent.blocked });
      showSnackbar(menuStudent.blocked ? 'Student unblocked' : 'Student blocked', 'success');
      fetchStudents();
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
    handleMenuClose();
  };

  const activeFilterCount = [
    filters.status !== 'all',
    filters.has_active_booking,
    !!filters.sort,
  ].filter(Boolean).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', pb: 14 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="800" gutterBottom>
            Students
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage students, payments, and notes
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={isSm ? null : <FileUpload />}
          onClick={() => setBulkImportOpen(true)}
          sx={{ borderRadius: RADIUS.md, whiteSpace: 'nowrap', minWidth: isSm ? 40 : 'auto', px: isSm ? 1 : 2 }}
          aria-label="Bulk import students"
        >
          {isSm ? <FileUpload fontSize="small" /> : 'Bulk Import'}
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by name, email, or phone..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS.lg,
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      {/* Filter row */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => setFilterDrawerOpen(true)}
          aria-label="Open filters"
          sx={{
            bgcolor: activeFilterCount > 0 ? 'primary.main' : 'action.hover',
            color: activeFilterCount > 0 ? 'white' : 'text.primary',
            '&:hover': {
              bgcolor: activeFilterCount > 0 ? 'primary.dark' : 'action.selected',
            },
          }}
        >
          <Badge badgeContent={activeFilterCount} color="error">
            <FilterList />
          </Badge>
        </IconButton>

        <Chip
          label="All"
          onClick={() => setFilters({ ...filters, status: 'all' })}
          color={filters.status === 'all' ? 'primary' : 'default'}
          variant={filters.status === 'all' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Active"
          onClick={() => setFilters({ ...filters, status: 'active' })}
          color={filters.status === 'active' ? 'success' : 'default'}
          variant={filters.status === 'active' ? 'filled' : 'outlined'}
          icon={<CheckCircle />}
        />
        <Chip
          label="Blocked"
          onClick={() => setFilters({ ...filters, status: 'blocked' })}
          color={filters.status === 'blocked' ? 'error' : 'default'}
          variant={filters.status === 'blocked' ? 'filled' : 'outlined'}
          icon={<Block />}
        />
      </Box>

      {/* Result count */}
      {!loading && !fetchError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {pagination.total} student{pagination.total !== 1 ? 's' : ''}
          {debouncedSearch ? ` matching "${debouncedSearch}"` : ''}
        </Typography>
      )}

      {/* List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : fetchError ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ErrorOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>Couldn't load students</Typography>
          <Button variant="outlined" onClick={fetchStudents} sx={{ borderRadius: RADIUS.md }}>
            Retry
          </Button>
        </Box>
      ) : students.length === 0 ? (
        debouncedSearch ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>No matches found</Typography>
            <Button variant="text" onClick={() => setSearchInput('')} sx={{ borderRadius: RADIUS.md }}>
              Clear search
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Person sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>No students yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add your first student to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setFormDrawerOpen(true)}
              sx={{ borderRadius: RADIUS.md, background: GRADIENTS.brand }}
            >
              Add Student
            </Button>
          </Box>
        )
      ) : (
        <List disablePadding>
          {students.map((student, idx) => (
            <React.Fragment key={student.id}>
              <ListItemButton
                onClick={() => openDetail(student)}
                sx={{
                  px: 1,
                  py: 1,
                  borderRadius: RADIUS.md,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 52 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: student.blocked ? 'error.main' : 'primary.main',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                    }}
                  >
                    {student.name?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {student.name}
                      </Typography>
                      {student.blocked && (
                        <Chip label="Blocked" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Stack direction="row" spacing={1} sx={{ mt: 0.25, overflow: 'hidden' }}>
                      {student.phone && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {student.phone}
                        </Typography>
                      )}
                      {student.city && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          · {student.city}
                        </Typography>
                      )}
                    </Stack>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />

                {/* Quick-action icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {student.phone && (
                    <IconButton
                      size="small"
                      component="a"
                      href={`tel:${student.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Call ${student.name}`}
                    >
                      <Phone fontSize="small" />
                    </IconButton>
                  )}
                  {student.phone && (
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://wa.me/${student.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`WhatsApp ${student.name}`}
                    >
                      <WhatsApp fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, student)}
                    aria-label="More options"
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              </ListItemButton>

              {idx < students.length - 1 && (
                <Divider component="li" sx={{ ml: 7 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={(_, page) => setPagination((p) => ({ ...p, page }))}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* FAB */}
      <Fab
        color="primary"
        aria-label="Add student"
        onClick={() => setFormDrawerOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 96,
          right: 16,
          background: GRADIENTS.brand,
        }}
      >
        <Add />
      </Fab>

      {/* Row overflow menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { openDetail(menuStudent); handleMenuClose(); }}>
          <Visibility sx={{ mr: 1.5, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => { setSelectedStudent(menuStudent); setFormDrawerOpen(true); handleMenuClose(); }}>
          <Edit sx={{ mr: 1.5, fontSize: 20 }} />
          Edit Student
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleBlockStudent} sx={{ color: 'error.main' }}>
          <Block sx={{ mr: 1.5, fontSize: 20 }} />
          {menuStudent?.blocked ? 'Unblock' : 'Block'} Student
        </MenuItem>
      </Menu>

      {/* Drawers */}
      {selectedStudent && (
        <StudentDetailDrawer
          open={detailDrawerOpen}
          onClose={() => { setDetailDrawerOpen(false); setDetailInitialTab(0); }}
          student={selectedStudent}
          onChanged={() => { fetchStudents(); }}
          showSnackbar={showSnackbar}
          initialTab={detailInitialTab}
        />
      )}

      <StudentFormDrawer
        open={formDrawerOpen && !detailDrawerOpen}
        onClose={() => { setFormDrawerOpen(false); setSelectedStudent(null); }}
        student={formDrawerOpen && selectedStudent && !detailDrawerOpen ? selectedStudent : null}
        onSuccess={handleStudentCreated}
        showSnackbar={showSnackbar}
      />

      <BulkImportDrawer
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onSuccess={fetchStudents}
        showSnackbar={showSnackbar}
      />

      <StudentFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        value={filters}
        onApply={(f) => { setFilters(f); setPagination((p) => ({ ...p, page: 1 })); }}
      />
    </Box>
  );
}

export default StudentManagement;
